// State + intent-relay setup for the multiplayer match view. Extracted from
// MultiplayerMatchView.tsx so the component file is mostly JSX.
//
// Bundles: useMpSimWorld, useMpMatch, useAttackToasts, useSounds,
// useDeployGarrison, useGameActions, useTestBridge, plus the wrappedSim that
// mirrors each sim mutation into an intent queued to the match server.

import type { SceneNode } from '@aod/protocol';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSounds } from '../../audio/useSounds.js';
import { useAttackToasts } from '../../sim/useAttackToasts.js';
import { type Selection, useTestBridge } from '../../testBridge/useTestBridge.js';
import { useDeployGarrison } from '../../useDeployGarrison.js';
import { useGameActions } from '../../useGameActions.js';
import { localSeatOf } from '../seat.js';
import type { MatchInfo } from '../types.js';
import { type WrappedSim, buildWrappedSim } from './buildWrappedSim.js';
import { useMpMatch } from './useMpMatch.js';
import { useMpSimWorld } from './useMpSimWorld.js';

export interface MpMatchState {
  readonly mpSim: ReturnType<typeof useMpSimWorld>;
  readonly mpMatch: ReturnType<typeof useMpMatch>;
  readonly selection: Selection;
  readonly setSelection: (s: Selection) => void;
  readonly hoveredTile: SceneNode | null;
  readonly setHoveredTile: (t: SceneNode | null) => void;
  readonly showDebug: boolean;
  readonly setShowDebug: (fn: (v: boolean) => boolean) => void;
  readonly chatVisible: boolean;
  readonly setChatVisible: (fn: (v: boolean) => boolean) => void;
  readonly settingsOpen: boolean;
  readonly setSettingsOpen: (v: boolean) => void;
  readonly helpOpen: boolean;
  readonly setHelpOpen: (v: boolean) => void;
  readonly attackToasts: ReturnType<typeof useAttackToasts>['toasts'];
  readonly dismissToast: ReturnType<typeof useAttackToasts>['dismiss'];
  readonly wrappedSim: WrappedSim;
  readonly handleMoveClick: (q: number, r: number) => void;
  readonly handleTileClick: ReturnType<typeof useGameActions>['handleTileClick'];
  readonly handleCityClick: ReturnType<typeof useGameActions>['handleCityClick'];
  readonly handleUnitClick: ReturnType<typeof useGameActions>['handleUnitClick'];
  readonly handleBackgroundClick: ReturnType<typeof useGameActions>['handleBackgroundClick'];
  readonly closeSelection: ReturnType<typeof useGameActions>['closeSelection'];
  readonly startNewMatch: ReturnType<typeof useGameActions>['startNewMatch'];
  readonly handleStartBuilding: ReturnType<typeof useGameActions>['handleStartBuilding'];
  readonly handleDeployGarrison: ReturnType<typeof useDeployGarrison>;
  readonly selected: SceneNode | null;
  readonly buildings: Array<{
    id: string;
    name: string;
    description: string;
    cost: number;
    buildTime: number;
  }>;
  readonly garrisonCount: number;
  /** True when the local sim's determinism hash diverged from the
   *  server's — surfaced as a "desync detected" indicator in the view. */
  readonly determinismMismatch: boolean;
  /** Set when the server reports match:end (or our slot times out).
   *  winnerId '' means we timed out / winner unknown. The view renders an
   *  end-of-match banner while this is non-null. */
  readonly matchEnded: { readonly winnerId: string } | null;
}

// Pulled into its own file (buildWrappedSim.ts) so this hook stays focused on state.

export function useMpMatchState(
  matchInfo: MatchInfo,
  onBackToMenu: () => void,
  onRematch?: () => void,
): MpMatchState {
  // The seat the match-server assigned (see ../seat.ts) — never a hard-coded 0.
  const localSeat = localSeatOf(matchInfo);
  const mpSim = useMpSimWorld(matchInfo, localSeat);
  const [showDebug, setShowDebug] = useState(import.meta.env.DEV);
  const [hoveredTile, setHoveredTile] = useState<SceneNode | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  const [chatVisible, setChatVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [matchEnded, setMatchEnded] = useState<{ winnerId: string } | null>(null);

  // Match-server connection + intent relay.
  const mpMatch = useMpMatch({
    matchInfo,
    localPlayerId: (matchInfo.localPlayerId || matchInfo.players[0]?.accountId) ?? '0',
    externalStep: mpSim.externalStep,
    applyFrame: mpSim.applyFrame,
    getDeterminismHash: mpSim.getDeterminismHash,
    loadSnapshot: mpSim.loadGame,
    // Before the match-server ever sent match:end, this jumped straight to
    // menu/rematch. Now the message actually arrives, so we surface a result
    // banner and let the player pick — see MultiplayerMatchView.
    onMatchEnd: useCallback((winnerId: string) => {
      setMatchEnded({ winnerId });
    }, []),
  });

  const { determinismMismatch } = mpMatch;

  const { toasts: attackToasts, dismiss: dismissToast } = useAttackToasts(
    mpSim.snapshot,
    localSeat,
  );
  useSounds(mpSim.events ?? []);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '`') setShowDebug((v) => !v);
      if (e.key === 'Escape') setSelection(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleDeployGarrison = useDeployGarrison(mpSim, localSeat);

  // Wrap sim actions to also relay intents to the match-server.
  const relayIntent = useCallback(
    (intent: Record<string, unknown>) => {
      mpMatch.queueIntent(intent);
    },
    [mpMatch],
  );

  const wrappedSim = useMemo(
    () => buildWrappedSim(mpSim, relayIntent, localSeat),
    [mpSim, relayIntent, localSeat],
  );

  const handleMoveClick = useCallback(
    (q: number, r: number) => {
      if (selection?.kind === 'unit') {
        wrappedSim.moveUnit(selection.id, q, r);
      }
    },
    [selection, wrappedSim],
  );

  // Re-use the same game action callbacks, but pointed at the wrapped sim.
  const {
    handleTileClick,
    handleCityClick,
    handleUnitClick,
    handleBackgroundClick,
    closeSelection,
    startNewMatch,
    handleStartBuilding,
  } = useGameActions({
    sim: wrappedSim,
    selection,
    setSelection,
    setLocalPlayerName: () => {},
  });

  const selected: SceneNode | null = useMemo(() => {
    if (!selection || !mpSim.snapshot) return null;
    const all = mpSim.snapshot.scene.children ?? [];
    return all.find((n) => n.id === selection.id) ?? null;
  }, [selection, mpSim.snapshot]);

  const buildings = useMemo(() => {
    const globals = mpSim.snapshot?.state.globals as
      | {
          buildings?: Array<{
            id: string;
            name: string;
            description: string;
            cost: number;
            buildTime: number;
          }>;
        }
      | undefined;
    return globals?.buildings ?? [];
  }, [mpSim.snapshot]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: selected derives from selection + snapshot; listing all three is redundant.
  const garrisonCount = useMemo(() => {
    if (!selected || !mpSim.snapshot) return 0;
    const sq = selected.props?.q as number | undefined;
    const sr = selected.props?.r as number | undefined;
    if (sq === undefined || sr === undefined) return 0;
    const ownerId = selected.props?.ownerId as number | undefined;
    return (
      mpSim.snapshot.scene.children?.filter(
        (n) =>
          n.type === 'unit' &&
          (n.props?.ownerId as number | undefined) === ownerId &&
          (n.props?.q as number | undefined) === sq &&
          (n.props?.r as number | undefined) === sr,
      ).length ?? 0
    );
  }, [selection, selected, mpSim.snapshot]);

  useTestBridge({
    sim: wrappedSim,
    localPlayerIndex: localSeat,
    selection,
    setSelection,
    attackToasts,
  });

  return {
    mpSim,
    mpMatch,
    selection,
    setSelection,
    hoveredTile,
    setHoveredTile,
    showDebug,
    setShowDebug,
    chatVisible,
    setChatVisible,
    settingsOpen,
    setSettingsOpen,
    helpOpen,
    setHelpOpen,
    attackToasts,
    dismissToast,
    wrappedSim,
    handleMoveClick,
    handleTileClick,
    handleCityClick,
    handleUnitClick,
    handleBackgroundClick,
    closeSelection,
    startNewMatch,
    handleStartBuilding,
    handleDeployGarrison,
    selected,
    buildings,
    garrisonCount,
    // Expose for the render's reconnecting/sync-issue indicators.
    determinismMismatch: determinismMismatch ?? false,
    matchEnded,
  };
}
